/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { ReactNode } from 'react';
import { TableRowProps } from '@chakra-ui/react';
import { gql, useMutation } from '@apollo/client';

import createContext from '../../support/helpers/createContext';
import { useShowApolloError } from '../../support/graphql/useShowApolloError';

import { GET_EMAILS_AND_ADDRESSES } from './graphql';
import { Email } from './types';
import type {
  DeleteEmail,
  DeleteEmailVariables,
} from './__generated__/DeleteEmail';
import type {
  DuplicateEmail,
  DuplicateEmailVariables,
} from './__generated__/DuplicateEmail';

const DELETE_EMAIL = gql`
  mutation DeleteEmail($id: String!) {
    deleteEmail(id: $id) {
      result
    }
  }
`;

const DUPLICATE_EMAIL = gql`
  mutation DuplicateEmail($id: String!) {
    duplicateEmail(id: $id) {
      id
    }
  }
`;

type Props = {
  email: Email;
  onClick: (email: Email) => void;
  onEmailDuplicated: () => void;
  render: (props?: TableRowProps) => ReactNode;
};

type RowContext = {
  email: Email;
  deleteEmail: () => void;
  isDeleting: boolean;
  duplicateEmail: () => void;
  isDuplicating: boolean;
};

const [useRowContext, Provider] = createContext<RowContext>('RowContext');

export { useRowContext };

export function EmailListRow(props: Props) {
  const { email, onClick, onEmailDuplicated, render } = props;
  const showApolloError = useShowApolloError();

  const [deleteEmail, { loading: isDeleting }] = useMutation<
    DeleteEmail,
    DeleteEmailVariables
  >(DELETE_EMAIL, {
    onError: showApolloError,
    refetchQueries: [GET_EMAILS_AND_ADDRESSES],
  });

  const [duplicateEmail, { loading: isDuplicating }] = useMutation<
    DuplicateEmail,
    DuplicateEmailVariables
  >(DUPLICATE_EMAIL, {
    onError: showApolloError,
    onCompleted: () => onEmailDuplicated(),
    refetchQueries: [GET_EMAILS_AND_ADDRESSES],
  });

  const isSaving = isDeleting || isDuplicating;

  const context = {
    email,
    deleteEmail: () => {
      deleteEmail({ variables: { id: email.id } });
    },
    isDeleting,
    duplicateEmail: () => {
      duplicateEmail({ variables: { id: email.id } });
    },
    isDuplicating,
  };

  return (
    <Provider value={context}>
      {render({
        cursor: isSaving ? 'not-allowed' : 'pointer',
        onClick: () => !isSaving && onClick(email),
      })}
    </Provider>
  );
}
